<template>
    <div class="">
        <!-- Main Card -->
        <div class="ppv2-py-4">
            <!-- DataTable Component -->
            <DataTable
                :perPage="itemsPerPage"
                :columns="tableColumns"
                :items="paginatedEquipements"
                :currentPage="currentPage"
                :totalPages="totalPages"
                :selectedItems="selectedItems"
                :enableDragDrop="enableDragDrop"
                :enableMultiSelect="true"
                rowClass="text-center"
                @page-changed="handlePageChange"
                @item-selected="toggleItemSelection"
                @select-all-toggled="toggleSelectAll"
                @sort="onSort"
                @drag-end="handleDragEnd"
            >
                <!-- Toolbar Slot -->
                <template #toolbar>
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="handleSearchUpdate" />
                        </div>

                        <!-- Export Button -->
                        <ExportSelect
                            :label="$t('equipements.actions.export')"
                            :options="exportOptions"
                            @export-selected="exportData"
                        />

                        <div class="ppv2-flex-1"></div>

                        <!-- Create Button -->
                        <BaseButton
                            v-if="selectedType === 'materiel' || selectedType === 'equipement'"
                            variant="purple"
                            :disabled="true"
                            :custom-height="40"
                            :custom-font-size="14"
                            custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                        >
                            <Plus :size="17" />
                            <span class="poppinsRegular">{{ $t('equipements.actions.create') }}</span>
                        </BaseButton>
                        <BaseButton
                            v-else
                            variant="purple"
                            :custom-height="40"
                            :custom-font-size="14"
                            custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                            @click="openCreateTab"
                        >
                            <Plus :size="17" />
                            <span class="poppinsRegular">{{
                                $t('equipements.actions.create_original') || $t('equipements.actions.create')
                            }}</span>
                        </BaseButton>
                    </div>
                </template>

                <!-- Column: Name (click opens edit popup) -->
                <template #column-name="{ item }">
                    <a
                        href="#"
                        @click.prevent.stop="openEditTab(item.id, item.type_equipement)"
                        class="ppv2-text-black poppinsRegular hover:ppv2-text-[#7D2DBD] ppv2-transition-colors hover:ppv2-underline ppv2-cursor-pointer"
                        :style="{ fontSize: normalizeFontSize(14) }"
                        :title="$t('equipements.actions.edit')"
                    >
                        {{ item.name }}
                    </a>
                </template>

                <!-- Column: Used By -->
                <template #column-prestations_count="{ item }">
                    <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                        {{
                            item.prestations_count > 0
                                ? item.prestations_count +
                                  ' ' +
                                  (item.prestations_count > 1
                                      ? $t('equipements.table.experiences_plural') || 'expériences'
                                      : $t('equipements.table.experience_singular') || 'expérience')
                                : '-'
                        }}
                    </span>
                </template>

                <!-- Column: Status -->
                <template #column-status="{ item }">
                    <button
                        @click="toggleStatus(item)"
                        class="ppv2-min-w-[125px] ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-transition-all"
                        :class="
                            item.status === 'actif'
                                ? 'ppv2-border-[#206A64] ppv2-bg-[#06B217] ppv2-bg-opacity-20'
                                : 'ppv2-border-[#4E4E4E] ppv2-bg-[#474747] ppv2-bg-opacity-20'
                        "
                    >
                        <div
                            class="ppv2-w-9 ppv2-h-5 ppv2-rounded-full ppv2-flex ppv2-items-center ppv2-transition-all"
                            :class="
                                item.status === 'actif'
                                    ? 'ppv2-bg-[#206A64] ppv2-justify-end'
                                    : 'ppv2-bg-[#4E4E4E] ppv2-justify-start'
                            "
                        >
                            <div class="ppv2-w-3.5 ppv2-h-3.5 ppv2-bg-white ppv2-rounded-full ppv2-mx-0.5"></div>
                        </div>
                        <span class="ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                            {{ item.status == 'actif' ? $t('equipements.status.active') : $t('equipements.status.inactive') }}
                        </span>
                    </button>
                </template>

                <!-- Column: Actions -->
                <template #column-actions="{ item }">
                    <div
                        class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3"
                        :style="{ fontSize: normalizeFontSize(18) }"
                    >
                        <button
                            @click.stop="openEditTab(item.id, item.type_equipement)"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('equipements.actions.edit')"
                        >
                            <SquarePen :size="18" />
                        </button>
                        <button
                            @click="duplicated(item.id, item.type_equipement)"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('equipements.actions.duplicate')"
                        >
                            <CopyPlus :size="18" />
                        </button>
                        <button
                            class="ppv2-p-1 ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('equipements.actions.delete')"
                            @click="deleteSingleItem(item.id)"
                        >
                            <Trash2 :size="18" />
                        </button>
                    </div>
                </template>

                <!-- Bulk Actions Slot -->
                <template #bulk-actions="{ selectedCount }">
                    <span class="poppinsRegular textGray" :style="{ fontSize: normalizeFontSize(15) }">
                        {{ $tc('equipements.selection.itemsSelected', selectedCount, { count: selectedCount }) }}
                    </span>
                    <span class="poppinsRegular ppv2-text-white ppv2-text-opacity-50" :style="{ fontSize: normalizeFontSize(15) }"
                        >|</span
                    >
                    <BaseButton
                        variant="outline"
                        @click="deleteSelectedItems"
                        customClass="!ppv2-px-4 !ppv2-h-[30px] !ppv2-flex !ppv2-items-center !ppv2-gap-2"
                    >
                        <Trash2 :size="14" />
                        <span>{{ $t('common.deleteSelection') }}</span>
                    </BaseButton>
                </template>
            </DataTable>
        </div>

        <!-- Resource Modal (Add/Edit) -->
        <ResourceModal
            v-model="showResourceModal"
            :resource-type="selectedType === 'tout' ? (itemToEdit ? itemToEdit.type_equipement : 'salle') : selectedType"
            :item="itemToEdit"
            :token="token"
            @save-success="fetchData"
        />

        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="deletePopupTitle" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p
                    class="ppv2-text-center ppv2-text-black poppinsRegular ppv2-whitespace-pre-line"
                    :style="{ fontSize: normalizeFontSize(15) }"
                >
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    id="button"
                    type="button"
                    variant="outline"
                    @click="showDeletePopup = false"
                    customClass="!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]"
                    :customHeight="45"
                >
                    {{ $t('equipements.deletePopup.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="confirmDelete"
                    customClass="!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]"
                    :customHeight="45"
                >
                    {{ $t('equipements.deletePopup.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>

        <!-- Disable Confirmation Popup — n'apparaît QUE si la ressource est utilisée -->
        <CustomDialog
            v-model="showDisablePopup"
            :defaultTitle="$t('equipements.disablePopup.title')"
            customClassTitle="poppinsSemiBold"
            noScroll
        >
            <template #content>
                <p
                    class="ppv2-text-center ppv2-text-black poppinsRegular ppv2-whitespace-pre-line"
                    :style="{ fontSize: normalizeFontSize(15) }"
                >
                    {{ disablePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    id="button"
                    type="button"
                    variant="outline"
                    @click="cancelDisable"
                    customClass="!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]"
                    :customHeight="45"
                >
                    {{ $t('equipements.disablePopup.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="confirmDisable"
                    customClass="!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]"
                    :customHeight="45"
                >
                    {{ $t('equipements.disablePopup.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import { CloudDownload, Plus, ChevronUp, CopyPlus, Trash2, GripVertical, SquarePen } from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Pagination from '../../Customer/Pagination.vue'
import Search from '../Experience/components/Search.vue'
import ExportSelect from '../../Shared/ExportSelect.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import BaseCheckbox from '../../Shared/BaseCheckbox.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import DataTable from '../../Shared/DataTable.vue'
import ResourceModal from '../Resource/components/ResourceModal.vue'
import { eventBus } from '../../Customer/eventBus'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { getPerPage, setPerPage } from '../../../utils/perPage'
export default {
    name: 'BoEquipementsV3Index',
    components: {
        CloudDownload,
        Plus,
        ChevronUp,
        SquarePen,
        CopyPlus,
        Trash2,
        GripVertical,
        Pagination,
        Search,
        ExportSelect,
        BaseButton,
        BaseCheckbox,
        CustomDialog,
        DataTable,
        ResourceModal,
    },
    props: {
        initialType: {
            type: String,
            default: 'tout',
        },
        token: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            searchQuery: '',
            selectedType: this.initialType, // Valeur par défaut pour le type
            currentPage: 1,
            itemsPerPage: getPerPage('equipements_items_per_page', 10),
            nbreOfPages: 1,
            nbreOfPages: 1,
            exportOptions: [
                { value: 'csv', label: this.$t('equipements.export.csv') },
                { value: 'xls', label: this.$t('equipements.export.xls') },
            ],

            selectedItems: [], // Tableau pour stocker les IDs des éléments sélectionnés
            selectAll: false, // État pour la case à cocher "Tout sélectionner"
            showDisablePopup: false,
            disablePopupDescription: '',
            itemToDisable: null, // ressource en attente de confirmation de désactivation
            showDeletePopup: false,
            deletePopupTitle: this.$t('equipements.deletePopup.title'),
            deletePopupDescription: '',
            deleteAction: null, // 'single' or 'multiple'
            itemToDelete: null, // ID of item to delete for single deletion
            equipements: {
                data: [],
                total: 0, // nombre total de pages (last_page)
            },
            sortKey: 'order',
            sortOrder: 'asc',
            isSavingPositions: false,
            showResourceModal: false,
            itemToEdit: null,
        }
    },

    computed: {
        enableDragDrop() {
            if (this.isSavingPositions) return false
            if (this.selectedType === 'tout') return false
            if (this.searchQuery && this.searchQuery.trim() !== '') return false
            if (this.sortKey !== 'order' || this.sortOrder !== 'asc') return false
            return true
        },
        tableColumns() {
            return [
                { key: 'name', label: this.$t('equipements.table.name'), sortable: true, type: 'text' },
                {
                    key: 'prestations_count',
                    label: this.$t('equipements.table.used_by') || 'Utilisé par',
                    sortable: true,
                    type: 'text',
                },
                { key: 'status', label: this.$t('equipements.table.status'), sortable: true, type: 'badge' },
                {
                    key: 'capacity',
                    label: this.$t('equipements.table.capacity'),
                    sortable: true,
                    type: 'number',
                },
                { key: 'actions', label: this.$t('equipements.table.actions'), sortable: false, type: 'actions' },
            ]
        },
        filteredEquipements() {
            const search = this.searchQuery?.toLowerCase().trim() || ''
            const type = this.selectedType

            if (!this.equipements?.data) return []

            return this.equipements.data.filter((e) => {
                const name = e.name?.toLowerCase() || ''
                const matchesSearch = !search || name.includes(search)
                const matchesType = !type || type === 'all' || type === 'tout' || e.type_equipement === type
                return matchesSearch && matchesType
            })
        },
        totalPages() {
            console.log('totalPAGE', this.equipements.total)
            return this.equipements.total
        },
        paginatedEquipements() {
            console.log('paginatedEquipements', this.equipements)
            return this.filteredEquipements || []
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        /**
         * Désactiver une ressource la conserve dans la liste avec un statut indisponible et laisse
         * les expériences qui l'utilisent configurées sur cette ressource. On avertit donc avant,
         * comme le fait déjà la suppression — mais uniquement quand des expériences VIVANTES
         * sont concernées : `active_prestations_count` exclut les archivées et les supprimées, là où
         * `prestations_count` les compterait (une salle affiche 219 liaisons pour 4 expériences
         * réelles).
         *
         * Réactiver n'avertit jamais : l'action est sans conséquence.
         */
        toggleStatus(item) {
            const isDisabling = item.status === 'actif'
            const linkedCount = item.active_prestations_count || 0

            if (isDisabling) {
                this.itemToDisable = { id: item.id, type: item.type_equipement }
                this.disablePopupDescription =
                    this.$t('equipements.disablePopup.message') +
                    '\n' +
                    (linkedCount > 0
                        ? this.$tc('equipements.disablePopup.usageWarning', linkedCount, { count: linkedCount })
                        : this.$t('equipements.disablePopup.unusedWarning'))
                this.showDisablePopup = true
                return
            }

            this.applyStatusToggle(item)
        },

        cancelDisable() {
            this.showDisablePopup = false
            this.itemToDisable = null
        },

        confirmDisable() {
            const pending = this.itemToDisable
            this.showDisablePopup = false
            this.itemToDisable = null

            if (!pending) return

            // Re-résolution depuis la liste : si la ressource a disparu entre-temps, on ne
            // touche à rien plutôt que d'agir sur une référence obsolète.
            const item = (this.equipements.data || []).find((e) => e.id === pending.id && e.type_equipement === pending.type)

            if (item) {
                this.applyStatusToggle(item)
            }
        },

        async applyStatusToggle(item) {
            const previousStatus = item.status
            const newStatus = previousStatus === 'actif' ? 'inactif' : 'actif'
            const type = item.type_equipement
            // Status 2 means inactive; deletion is represented separately by deleted_at.
            const newActive = type == 'salle' ? (newStatus === 'actif' ? 1 : 2) : newStatus === 'actif' ? 1 : 0
            // Optimistic UI update
            item.status = newStatus

            try {
                const response = await axios.patch(
                    '/api/v3/equipements/update-status',
                    {
                        id: item.id,
                        active: newActive,
                        type: type,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                        },
                    }
                )

                if (!response.data.success) {
                    // Revert on API failure
                    item.status = previousStatus
                    alert('Erreur lors de la mise à jour du statut')
                    return
                }

                await this.fetchData()
                this.$emit('refresh-stats')
            } catch (error) {
                // Revert AVANT toute notification : sur une coupure réseau ou une réponse HTML,
                // `error.response` est absent et déréférencer `.data.message` lèverait ici, laissant
                // la bascule optimiste en place.
                item.status = previousStatus
                this.$toast.error(error?.response?.data?.message ?? this.$t('equipements.notifications.statusError'))
            }
        },
        getTypeLabel(type) {
            const labels = {
                salle: this.$t('equipements.types.salle'),
                materiel: this.$t('equipements.types.materiel'),
                equipement: this.$t('equipements.types.equipement'),
            }
            return labels[type] || type
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        async handleDragEnd(event) {
            const { oldIndex, newIndex } = event

            if (oldIndex === newIndex) return

            if (this.isSavingPositions) {
                this.$toast?.info?.(this.$t('equipements.notifications.savingPositions'))
                return
            }

            this.isSavingPositions = true

            try {
                // L'ordre logique AVANT le glissé est la liste affichée : le composant
                // DataTable réordonne le DOM, pas ce tableau. On reconstruit donc la liste
                // permutée pour retrouver le VOISIN du nouvel emplacement.
                const filteredList = [...this.paginatedEquipements]
                const [movedItem] = filteredList.splice(oldIndex, 1)
                filteredList.splice(newIndex, 0, movedItem)

                const previous = filteredList[newIndex - 1]
                const next = filteredList[newIndex + 1]
                const anchor = previous || next

                if (!anchor) return

                await axios.patch(
                    '/api/v3/equipements/reorder',
                    {
                        moved: { id: movedItem.id, type: movedItem.type_equipement },
                        anchor: { id: anchor.id, type: anchor.type_equipement },
                        placement: previous ? 'after' : 'before',
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                        },
                    }
                )

                await this.fetchData()
                this.$toast.success(this.$t('equipements.notifications.positions_updated'))
            } catch (error) {
                this.$toast.error(error?.response?.data?.message ?? this.$t('equipements.notifications.positionsError'))
                await this.fetchData()
            } finally {
                this.isSavingPositions = false
            }
        },
        handleSearchUpdate(searchValue) {
            this.searchQuery = searchValue
            this.currentPage = 1
            this.fetchData()
        },

        // Méthodes pour la sélection multiple
        toggleItemSelection(itemId) {
            const index = this.selectedItems.indexOf(itemId)
            if (index > -1) {
                // Retirer l'élément de la sélection
                this.selectedItems.splice(index, 1)
            } else {
                // Ajouter l'élément à la sélection
                this.selectedItems.push(itemId)
            }
        },

        toggleSelectAll() {
            this.selectAll = !this.selectAll
            if (this.selectAll) {
                // Sélectionner tous les éléments de la page courante
                this.selectedItems = this.paginatedEquipements.map((item) => item.id)
            } else {
                // Désélectionner tout
                this.selectedItems = []
            }
        },

        deleteSelectedItems() {
            if (this.selectedItems.length === 0) return

            this.deleteAction = 'multiple'
            const linkedItems = this.equipements.data.filter((e) => this.selectedItems.includes(e.id) && e.prestations_count > 0)
            const totalPrestations = linkedItems.reduce((acc, curr) => acc + curr.prestations_count, 0)

            this.deletePopupDescription = this.$t('equipements.deletePopup.message')
            if (totalPrestations > 0) {
                const warningKey =
                    this.selectedItems.length > 1
                        ? 'equipements.deletePopup.deleteWarningMultiple'
                        : 'equipements.deletePopup.deleteWarning'
                this.deletePopupDescription += '\n' + this.$t(warningKey, { count: totalPrestations })
            }
            this.showDeletePopup = true
        },

        deleteSingleItem(itemId) {
            this.deleteAction = 'single'
            this.itemToDelete = itemId
            const item = this.equipements.data.find((e) => e.id === itemId)

            this.deletePopupDescription = this.$t('equipements.deletePopup.message')
            if (item && item.prestations_count > 0) {
                this.deletePopupDescription +=
                    '\n' + this.$t('equipements.deletePopup.deleteWarning', { count: item.prestations_count })
            }
            this.showDeletePopup = true
        },

        async confirmDelete() {
            if (this.deleteAction === 'multiple') {
                try {
                    await axios.delete('/api/v3/equipements/delete', {
                        data: {
                            ids: this.selectedItems,
                            type: this.selectedType,
                        },
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                        },
                    })

                    // Rafraîchir la liste après suppression
                    await this.fetchData()

                    // Réinitialiser la sélection
                    this.selectedItems = []
                    this.selectAll = false
                    this.showDeletePopup = false
                    this.$toast.success(this.$t('equipements.deletePopup.deleteSuccess'))
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                    console.error('Delete error:', error)
                    alert(this.$t('common.error'))
                }
            } else if (this.deleteAction === 'single' && this.itemToDelete) {
                try {
                    const itemId = this.itemToDelete
                    await axios.delete('/api/v3/equipements/delete', {
                        data: {
                            ids: [itemId],
                            type: this.selectedType,
                        },
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                        },
                    })

                    // Rafraîchir la liste après suppression
                    await this.fetchData()
                    // Retirer également de la sélection si présent
                    this.selectedItems = this.selectedItems.filter((id) => id !== itemId)
                    this.showDeletePopup = false
                    this.$toast.success(this.$t('equipements.deletePopup.deleteSuccess'))
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                    console.error('Delete error:', error)
                    alert(this.$t('common.error'))
                }
            }
        },

        handlePerPageChange(perPage) {
            console.log('handlePerPageChange called', perPage)
            this.itemsPerPage = Number(perPage)
            setPerPage('equipements_items_per_page', this.itemsPerPage)
            this.fetchData()
        },
        async fetchData() {
            this.isLoading = true
            this.error = null
            console.log('searchQuery', this.searchQuery)

            try {
                const url = `/api/v3/equipements/list?search=${this.searchQuery}&type=${this.selectedType}&currentPage=${this.currentPage}&itemsPerPage=${this.itemsPerPage}&sort_by=${this.sortKey}&sort_order=${this.sortOrder}`
                const res = await fetch(url, {
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })
                const json = await res.json()
                console.log('ws', json.last_page)
                this.equipements = {
                    data: json.data || [],
                    total: json.last_page || 0,
                }

                // Mettre à jour l'état de pagination à partir de la réponse backend
                if (typeof json.current_page === 'number') {
                    this.currentPage = json.current_page
                }
                if (typeof json.per_page === 'number') {
                    this.itemsPerPage = json.per_page
                }
                if (typeof json.last_page === 'number') {
                    this.nbreOfPages = json.last_page
                }

                console.log('equipements', this.equipements)
            } catch (err) {
                this.error = err.message
                this.$toast.error(err?.message || 'Erreur lors du chargement des équipements')
                console.error('API error:', err)
            } finally {
                this.isLoading = false
            }
        },
        async exportData(format) {
            try {
                const response = await axios.get(
                    `/api/v3/equipements/export?format=${format}&search=${this.searchQuery}&type=${this.selectedType}`,
                    {
                        responseType: 'blob',
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                        },
                    }
                )

                const blob = new Blob([response.data])
                const url = window.URL.createObjectURL(blob)

                const link = document.createElement('a')
                link.href = url

                const fileName = `export.${format}`
                link.setAttribute('download', fileName)

                document.body.appendChild(link)
                link.click()
                link.remove()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Export error:', error)
            }
        },
        duplicated(itemId, type) {
            this.duplicatedData(itemId, type)
        },
        async duplicatedData(id, type) {
            try {
                const response = await axios.post(
                    `/api/v3/equipements/duplicate`,
                    {
                        id: id,
                        type: type,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${this.token}`,
                        },
                    }
                )

                const index = this.equipements.data.findIndex((exp) => exp.id === id)
                if (index !== -1) {
                    const itemToDuplicate = this.equipements.data[index]
                    const newItem = { ...itemToDuplicate, id: response.data.id, name: response.data.name }
                    this.equipements.data.unshift(newItem)
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Duplication error:', error)
            }
        },
        openEditTab(itemId, type) {
            this.itemToEdit =
                this.equipements.data.find((e) => e.id === itemId && (!type || e.type_equipement === type)) ||
                this.equipements.data.find((e) => e.id === itemId)
            this.$nextTick(() => {
                this.showResourceModal = true
            })
        },
        openCreateTab() {
            this.itemToEdit = null
            this.$nextTick(() => {
                this.showResourceModal = true
            })
        },
        onSort(key) {
            if (this.sortKey === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortKey = key
                this.sortOrder = 'asc'
            }
            this.currentPage = 1
            this.fetchData()
        },
    },
    mounted() {
        eventBus.$on('dt-selected-perpage-changed', this.handlePerPageChange)
        this.fetchData()
    },
    beforeDestroy() {},
    watch: {
        // Mettre à jour l'état "selectAll" quand la sélection change
        selectedItems: {
            handler(newSelection) {
                const currentPageIds = this.paginatedEquipements.map((item) => item.id)
                this.selectAll = currentPageIds.length > 0 && currentPageIds.every((id) => newSelection.includes(id))
            },
            deep: true,
        },

        // Réinitialiser la sélection quand on change de page
        currentPage() {
            this.selectedItems = []
            this.selectAll = false
        },

        // Réinitialiser la sélection quand on filtre ou recherche
        filteredEquipements() {
            this.selectedItems = []
            this.selectAll = false
        },
    },
}
</script>

<style scoped lang="scss">
.textGray {
    color: #1a1f37;
}

.bgMateriel {
    background-color: rgba(0, 117, 255, var(--tw-bg-opacity));
}

.bgInstallation {
    background-color: rgba(219, 168, 0, var(--tw-bg-opacity));
}

.dark {
    .textGray {
        color: #bfbfbf;
    }
}

.headerColor {
    background-color: #7d2dbd26;
}
</style>
